import {createSlice, type PayloadAction} from '@reduxjs/toolkit';

import type {SsoLinkStatus} from '@/services/icc/sso/ssoLinkStatuses';

export type SignedMediaListItem = {
    mediaId: string;
    url?: string;
    expirationDate?: number;
};

export type SsoLinkEntry = {
    url?: string;
    directUrl?: string;
    expirationDate?: number;
    status?: SsoLinkStatus;
};

export type SsoLinksState = {
    byTemplate: Record<string, SsoLinkEntry>;
    byUrl: Record<string, SsoLinkEntry>;
};

export type AddSsoLinkPayload = {
    ssoTemplate: string;
} & Partial<SsoLinkEntry>;

export type MediaState = {
    signedMediaList: SignedMediaListItem[];
    ssoLinks: SsoLinksState;
    lastSsoSessionApplyAt: number | null;
};

const initialState: MediaState = {
    signedMediaList: [],
    ssoLinks: {byTemplate: {}, byUrl: {}},
    lastSsoSessionApplyAt: null,
};

const upsertSsoLink = (state: MediaState, payload: AddSsoLinkPayload, target: keyof SsoLinksState): void => {
    const {ssoTemplate, url, directUrl, expirationDate, status} = payload;

    state.ssoLinks[target][ssoTemplate] = {
        url,
        directUrl,
        expirationDate,
        status,
    };
};

export const slice = createSlice({
    name: 'media',
    initialState,
    reducers: {
        addSignedMedia: (state, action: PayloadAction<SignedMediaListItem[]>) => {
            const nextItems = action.payload;

            state.signedMediaList = [
                ...state.signedMediaList.filter(
                    ({mediaId}) => !nextItems.some((nextItem) => mediaId === nextItem.mediaId)
                ),
                ...nextItems,
            ];
        },
        addSsoTemplateLink: (state, action: PayloadAction<AddSsoLinkPayload>) => {
            upsertSsoLink(state, action.payload, 'byTemplate');
        },
        addSsoUrlLink: (state, action: PayloadAction<AddSsoLinkPayload>) => {
            upsertSsoLink(state, action.payload, 'byUrl');
        },
        invalidateSsoLink: (state, action: PayloadAction<{ssoTemplate: string}>) => {
            const {ssoTemplate} = action.payload;

            if (state.ssoLinks.byTemplate[ssoTemplate]) {
                delete state.ssoLinks.byTemplate[ssoTemplate];
            }

            if (state.ssoLinks.byUrl[ssoTemplate]) {
                delete state.ssoLinks.byUrl[ssoTemplate];
            }
        },
        recordSsoSessionApplied: (state) => {
            state.lastSsoSessionApplyAt = Date.now();
        },
    },
});

export const {addSignedMedia, addSsoTemplateLink, addSsoUrlLink, invalidateSsoLink, recordSsoSessionApplied} =
    slice.actions;

export default slice.reducer;
